import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  GripVertical
} from 'lucide-react';

// Custom Autocomplete Input for Mobile
const AutocompleteInput = ({ value, onChange, suggestions, placeholder, testId }) => {
  // Dropdown is only shown while the user is actively typing in THIS input.
  // Selecting a suggestion, blurring, or interacting with any other element closes it,
  // and it will not reopen until the user starts typing (or clears the field) again.
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const filteredSuggestions = (value && value.length > 0)
    ? suggestions.filter(s =>
        s.toLowerCase().includes(value.toLowerCase()) &&
        s.toLowerCase() !== value.toLowerCase()
      ).slice(0, 6)
    : [];

  const isOpen = isTyping && filteredSuggestions.length > 0;

  // Close dropdown on any interaction outside this input (mouse or touch).
  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsTyping(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, []);

  const handleSelect = (suggestion) => {
    setIsTyping(false);
    onChange(suggestion);
    inputRef.current?.blur();
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => {
          setIsTyping(true);
          onChange(e.target.value);
        }}
        onBlur={() => setIsTyping(false)}
        className="virsa-input h-12 text-base w-full"
        placeholder={placeholder}
        autoComplete="off"
        data-testid={testId}
      />
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filteredSuggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onMouseDown={(e) => e.preventDefault()} // keep focus for onClick to fire before blur
              onClick={() => handleSelect(suggestion)}
              className="w-full px-4 py-3 text-left text-base hover:bg-stone-100 border-b border-stone-100 last:border-b-0 transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format quantity with up to 5 decimal places, removing trailing zeros
const formatQuantity = (num) => {
  if (num === 0 || num === null || num === undefined || isNaN(num)) return '0';
  
  // Round to 5 decimal places
  const rounded = Math.round(num * 100000) / 100000;
  
  // Convert to string with up to 5 decimal places
  let result = rounded.toFixed(5);
  
  // Remove trailing zeros but keep at least 2 decimal places for readability
  result = result.replace(/\.?0+$/, '');
  
  // If no decimal point, add .00 for whole numbers > 1, or show as-is for small numbers
  if (!result.includes('.')) {
    if (Math.abs(rounded) >= 1) {
      result = rounded.toFixed(2);
    }
  } else {
    // Ensure at least 2 decimal places for numbers >= 1
    const parts = result.split('.');
    if (Math.abs(rounded) >= 1 && parts[1] && parts[1].length < 2) {
      result = rounded.toFixed(2);
    }
  }
  
  return result;
};

const DEFAULT_CATEGORIES = [
  { value: 'Daal', label: 'Daal' },
  { value: 'Sabzi', label: 'Sabzi' },
  { value: 'Salad', label: 'Salad' },
  { value: 'Sweet', label: 'Sweet' },
  { value: 'SpecialMain', label: 'Special Main' },
  { value: 'SpecialRoti', label: 'Special Roti' },
  { value: 'SpecialRaita', label: 'Special Raita' },
  { value: 'SpecialSide', label: 'Special Side' },
  { value: 'Other', label: 'Other' }
];

const VISIBILITY_TAGS = [
  { value: 'Regular Rotation Eligible', label: 'Regular Rotation' },
  { value: 'Special Rotation Eligible', label: 'Special Rotation' }
];

const UNITS = ['kg', 'g', 'L', 'ml', 'bucket', 'spoon', 'pcs', 'cup', 'tbsp', 'tsp'];

export const RecipeForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [ingredients, setIngredients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Unit management
  const [availableUnits, setAvailableUnits] = useState(UNITS);
  const [showNewUnitDialog, setShowNewUnitDialog] = useState(false);
  const [newUnitName, setNewUnitName] = useState('');
  const [pendingUnitIndex, setPendingUnitIndex] = useState(null);

  // Category management
  const [availableCategories, setAvailableCategories] = useState(DEFAULT_CATEGORIES);
  const [showNewCategoryDialog, setShowNewCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Recipe data
  const [recipeName, setRecipeName] = useState('');
  const [category, setCategory] = useState('Sabzi');
  const [visibilityTag, setVisibilityTag] = useState('Regular Rotation Eligible');
  const [active, setActive] = useState(true);
  const [cookingSteps, setCookingSteps] = useState('');
  const [importantNotes, setImportantNotes] = useState('');
  
  // Batch info for scaling
  const [batchTiffinCount, setBatchTiffinCount] = useState(1);
  const [originalPerTiffinQtys, setOriginalPerTiffinQtys] = useState([]); // Store original per-tiffin quantities
  
  // Recipe ingredients (with batch quantities)
  const [recipeIngredients, setRecipeIngredients] = useState([]);

  const fetchIngredients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/ingredients`);
      setIngredients(response.data);
    } catch (error) {
      toast.error('Failed to fetch ingredients');
    }
  }, []);

  const fetchUnits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/units`);
      const custom = response.data || [];
      setAvailableUnits(prev => {
        const merged = [...prev];
        custom.forEach(u => {
          if (!merged.some(m => m.toLowerCase() === u.toLowerCase())) merged.push(u);
        });
        return merged;
      });
    } catch (error) {
      // Non-blocking: units endpoint may not be reachable, keep defaults
    }
  }, []);

  const fetchRecipe = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/recipes/${id}`);
      const recipe = response.data;
      setRecipeName(recipe.recipe_name);
      setCategory(recipe.category);
      setVisibilityTag(recipe.visibility_tag);
      setActive(recipe.active);
      setCookingSteps(recipe.cooking_steps || '');
      setImportantNotes(recipe.important_notes || '');
      // Load as 1 tiffin - show the actual per-tiffin quantities
      setBatchTiffinCount(1);
      
      // Store original per-tiffin quantities for scaling
      const perTiffinQtys = recipe.ingredients.map(ing => ing.qty_per_tiffin);
      setOriginalPerTiffinQtys(perTiffinQtys);
      
      // Add any units from recipe that aren't in the default list
      const recipeUnits = recipe.ingredients.map(ing => ing.unit).filter(u => u && u.trim());
      const uniqueUnits = [...new Set([...UNITS, ...recipeUnits])];
      setAvailableUnits(uniqueUnits);
      
      setRecipeIngredients(recipe.ingredients.map(ing => ({
        ...ing,
        batch_qty: ing.qty_per_tiffin,  // Show per-tiffin qty directly (for 1 tiffin)
        _key: crypto.randomUUID()
      })));
    } catch (error) {
      toast.error('Failed to fetch recipe');
      navigate('/recipes');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchIngredients();
    fetchUnits();
    if (isEditing) {
      fetchRecipe();
    } else {
      // Initialize with 10 empty ingredient lines for new recipes
      const initialIngredients = Array.from({ length: 10 }, (_, i) => ({
        ingredient_name: '',
        batch_qty: '',
        unit: 'g',
        ingredient_line_order: i + 1,
        _key: crypto.randomUUID()
      }));
      setRecipeIngredients(initialIngredients);
      setLoading(false);
    }
  }, [id, isEditing, fetchIngredients, fetchUnits, fetchRecipe]);

  // Handle batch tiffin count change - scale quantities proportionally
  const handleBatchCountChange = (newCount) => {
    if (newCount === '') {
      setBatchTiffinCount('');
      return;
    }
    const count = parseInt(newCount);
    if (isNaN(count) || count < 0) return;
    
    setBatchTiffinCount(count);
    
    // Scale batch quantities based on original per-tiffin values
    if (isEditing && originalPerTiffinQtys.length > 0 && count >= 1) {
      setRecipeIngredients(prev => prev.map((ing, index) => ({
        ...ing,
        batch_qty: index < originalPerTiffinQtys.length 
          ? originalPerTiffinQtys[index] * count 
          : ing.batch_qty
      })));
    }
  };

  const addIngredientLines = () => {
    // Add 5 more ingredient lines
    const newLines = Array.from({ length: 5 }, (_, i) => ({
      ingredient_name: '',
      batch_qty: '',
      unit: 'g',
      ingredient_line_order: recipeIngredients.length + i + 1,
      _key: crypto.randomUUID()
    }));
    setRecipeIngredients([...recipeIngredients, ...newLines]);
  };

  const updateIngredientLine = (index, field, value) => {
    const updated = [...recipeIngredients];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-fill unit from master ingredient list
    if (field === 'ingredient_name') {
      const masterIng = ingredients.find(i => i.name.toLowerCase() === value.toLowerCase());
      if (masterIng) {
        updated[index].unit = masterIng.default_unit;
      }
    }
    
    setRecipeIngredients(updated);
  };

  const removeIngredientLine = (index) => {
    const updated = recipeIngredients.filter((_, i) => i !== index);
    updated.forEach((ing, i) => {
      ing.ingredient_line_order = i + 1;
    });
    setRecipeIngredients(updated);
  };

  const handleAddNewUnit = async () => {
    const trimmedUnit = newUnitName.trim();
    if (!trimmedUnit) {
      toast.error('Please enter a unit name');
      return;
    }
    
    // Check if unit already exists
    if (availableUnits.some(u => u.toLowerCase() === trimmedUnit.toLowerCase())) {
      toast.error('This unit already exists');
      return;
    }
    
    // Add the new unit to available units
    setAvailableUnits(prev => [...prev, trimmedUnit]);
    
    // Persist for future recipes
    try {
      await axios.post(`${API}/units`, { name: trimmedUnit });
    } catch (e) {
      // Non-blocking; unit still usable for this session
    }
    
    // If there's a pending ingredient line, update its unit
    if (pendingUnitIndex !== null) {
      updateIngredientLine(pendingUnitIndex, 'unit', trimmedUnit);
    }
    
    // Close dialog and reset
    setShowNewUnitDialog(false);
    setNewUnitName('');
    setPendingUnitIndex(null);
    toast.success(`Unit "${trimmedUnit}" added`);
  };

  const handleAddNewCategory = () => {
    const trimmedCategory = newCategoryName.trim();
    if (!trimmedCategory) {
      toast.error('Please enter a category name');
      return;
    }
    
    // Check if category already exists
    if (availableCategories.some(c => c.value.toLowerCase() === trimmedCategory.toLowerCase())) {
      toast.error('This category already exists');
      return;
    }
    
    // Add the new category
    const newCat = { value: trimmedCategory, label: trimmedCategory };
    setAvailableCategories(prev => [...prev, newCat]);
    setCategory(trimmedCategory);
    
    // Close dialog and reset
    setShowNewCategoryDialog(false);
    setNewCategoryName('');
    toast.success(`Category "${trimmedCategory}" added`);
  };

  const handleSave = async () => {
    if (!recipeName.trim()) {
      toast.error('Recipe name is required');
      return;
    }

    if (batchTiffinCount <= 0) {
      toast.error('Batch tiffin count must be greater than 0');
      return;
    }

    setSaving(true);
    try {
      // First, check for new ingredients and add them to master list
      const existingNames = ingredients.map(i => i.name.toLowerCase());
      const newIngredients = [];
      const unitUpdates = []; // {id, name, default_unit, master_sort_order}
      
      for (const ing of recipeIngredients) {
        const trimmedName = ing.ingredient_name.trim();
        if (!trimmedName) continue;
        const nameLower = trimmedName.toLowerCase();
        if (!existingNames.includes(nameLower)) {
          newIngredients.push({
            name: trimmedName,
            default_unit: ing.unit,
            master_sort_order: 9999
          });
        } else if (ing.unit && ing.unit.trim()) {
          // If unit differs from master's default_unit, remember it for update
          const master = ingredients.find(i => i.name.toLowerCase() === nameLower);
          if (master && master.default_unit !== ing.unit && !unitUpdates.some(u => u.id === master.id)) {
            unitUpdates.push({
              id: master.id,
              name: master.name,
              default_unit: ing.unit,
              master_sort_order: master.master_sort_order
            });
          }
        }
      }

      // Create new ingredients
      for (const newIng of newIngredients) {
        await axios.post(`${API}/ingredients`, newIng);
      }

      // Update existing ingredients whose unit changed (auto-link ingredient ↔ unit)
      for (const u of unitUpdates) {
        try {
          await axios.put(`${API}/ingredients/${u.id}`, {
            name: u.name,
            default_unit: u.default_unit,
            master_sort_order: u.master_sort_order
          });
        } catch (e) {
          // Non-blocking; recipe save still succeeds
        }
      }

      if (newIngredients.length > 0) {
        toast.success(`Added ${newIngredients.length} new ingredient(s) to master list`);
      }

      // Calculate per-tiffin quantities
      const safeBatchCount = parseInt(batchTiffinCount) >= 1 ? parseInt(batchTiffinCount) : 1;
      const scaledIngredients = recipeIngredients
        .filter(ing => ing.ingredient_name.trim())
        .map(ing => ({
          ingredient_name: ing.ingredient_name.trim(),
          qty_per_tiffin: Math.round((parseFloat(ing.batch_qty) || 0) / safeBatchCount * 100) / 100,
          unit: ing.unit,
          ingredient_line_order: ing.ingredient_line_order
        }));

      const payload = {
        recipe_name: recipeName.trim(),
        category,
        visibility_tag: visibilityTag,
        active,
        cooking_steps: cookingSteps.trim(),
        important_notes: importantNotes.trim(),
        ingredients: scaledIngredients
      };

      if (isEditing) {
        await axios.put(`${API}/recipes/${id}`, payload);
        toast.success('Recipe updated');
      } else {
        await axios.post(`${API}/recipes`, payload);
        toast.success('Recipe created');
      }
      
      navigate('/recipes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recipe');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="recipe-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/recipes')}
          className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-stone-800">
            {isEditing ? 'Edit Recipe' : 'New Recipe'}
          </h1>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
          data-testid="save-recipe-btn"
        >
          {saving ? <span className="spinner" /> : <Save size={20} />}
          <span>Save Recipe</span>
        </button>
      </div>

      {/* Basic Info */}
      <div className="virsa-card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-stone-600 mb-2">
              Recipe Name
            </label>
            <input
              type="text"
              value={recipeName}
              onChange={(e) => setRecipeName(e.target.value)}
              className="virsa-input text-lg"
              placeholder="e.g., Rajma Masala"
              data-testid="recipe-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-600 mb-2">
              Category
            </label>
            <Select 
              value={category} 
              onValueChange={(v) => {
                if (v === '__add_new_category__') {
                  setNewCategoryName('');
                  setShowNewCategoryDialog(true);
                } else {
                  setCategory(v);
                }
              }}
            >
              <SelectTrigger className="h-12" data-testid="recipe-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableCategories.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
                <SelectItem value="__add_new_category__" className="text-[#124242] font-medium border-t mt-1 pt-1">
                  <span className="flex items-center gap-1">
                    <Plus size={14} />
                    Add new category...
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-600 mb-2">
              Rotation Type
            </label>
            <Select value={visibilityTag} onValueChange={setVisibilityTag}>
              <SelectTrigger className="h-12" data-testid="recipe-visibility-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VISIBILITY_TAGS.map(tag => (
                  <SelectItem key={tag.value} value={tag.value}>{tag.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <input
            type="checkbox"
            id="active"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="w-5 h-5 rounded border-stone-300 text-[#124242] focus:ring-[#124242]"
          />
          <label htmlFor="active" className="text-sm font-medium text-stone-600">
            Active (available for rotation)
          </label>
        </div>
      </div>

      {/* Batch Info */}
      <div className="virsa-card p-6 bg-[#124242]/5 border-[#124242]/20">
        <div className="flex items-center gap-4 flex-wrap">
          <label className="text-sm font-medium text-stone-700">
            {isEditing ? 'Edit quantities for' : 'This recipe was made for'}
          </label>
          <input
            type="number"
            min="1"
            value={batchTiffinCount}
            onChange={(e) => handleBatchCountChange(e.target.value)}
            onBlur={(e) => {
              if (e.target.value === '' || parseInt(e.target.value) < 1) {
                handleBatchCountChange('1');
              }
            }}
            className="virsa-input w-28 text-center text-lg font-bold"
            data-testid="batch-tiffin-count"
          />
          <label className="text-sm font-medium text-stone-700">
            tiffins
          </label>
          <span className="text-sm text-stone-500 ml-auto">
            {isEditing 
              ? '(Enter batch size - quantities will scale automatically)' 
              : '(System will auto-calculate per-tiffin amounts)'}
          </span>
        </div>
      </div>

      {/* Ingredients */}
      <div className="virsa-card p-4 md:p-6">
        <h2 className="text-xl font-bold text-stone-800 text-center mb-6">Ingredients</h2>

        {/* Ingredient Lines */}
        <div className="space-y-4 md:space-y-3">
          {/* Header - Desktop Only */}
          <div className="hidden md:grid grid-cols-12 gap-2 px-2 text-xs font-semibold text-stone-500 uppercase">
            <div className="col-span-1"></div>
            <div className="col-span-4">Ingredient</div>
            <div className="col-span-2 text-center">Batch Qty</div>
            <div className="col-span-2 text-center">Unit</div>
            <div className="col-span-2 text-center text-[#124242]">Per Tiffin</div>
            <div className="col-span-1"></div>
          </div>

          {recipeIngredients.map((ing, index) => (
            <div 
              key={ing._key || `line-${index}`} 
              className="md:bg-stone-50 md:rounded-lg md:p-3"
            >
              {/* Mobile Layout - Card Style with Clear Separation */}
              <div className="md:hidden">
                <div className="bg-white border-2 border-stone-200 rounded-xl p-4 shadow-sm">
                  {/* Card Header with Number and Delete */}
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-stone-100">
                    <span className="bg-[#124242] text-white text-xs font-bold px-2.5 py-1 rounded-full">
                      #{index + 1}
                    </span>
                    <button
                      onClick={() => removeIngredientLine(index)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid={`remove-ingredient-${index}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  
                  {/* Ingredient Name - Full Width with Type-ahead Suggestions */}
                  <div className="mb-4">
                    <label className="text-xs font-semibold text-stone-600 mb-1.5 block uppercase tracking-wide">
                      Item Name
                    </label>
                    <AutocompleteInput
                      value={ing.ingredient_name}
                      onChange={(val) => updateIngredientLine(index, 'ingredient_name', val)}
                      suggestions={ingredients.map(i => i.name)}
                      placeholder="e.g., Onion, Tomato, Dal"
                      testId={`ingredient-name-${index}`}
                    />
                  </div>
                  
                  {/* Quantity, Unit, Per Tiffin in a Row */}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-stone-600 mb-1.5 block uppercase tracking-wide">
                        Qty
                      </label>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={ing.batch_qty}
                        onChange={(e) => updateIngredientLine(index, 'batch_qty', e.target.value)}
                        className="virsa-input h-12 text-center text-base font-semibold w-full"
                        placeholder="0"
                        data-testid={`ingredient-qty-${index}`}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-stone-600 mb-1.5 block uppercase tracking-wide">
                        Unit
                      </label>
                      <Select
                        value={ing.unit || ''}
                        onValueChange={(v) => {
                          if (v === '__add_new__') {
                            setPendingUnitIndex(index);
                            setNewUnitName('');
                            setShowNewUnitDialog(true);
                          } else {
                            updateIngredientLine(index, 'unit', v);
                          }
                        }}
                      >
                        <SelectTrigger className="h-12 text-base" data-testid={`ingredient-unit-${index}`}>
                          <SelectValue placeholder="Unit">
                            {ing.unit || 'Unit'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {availableUnits.map(unit => (
                            <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                          ))}
                          <SelectItem value="__add_new__" className="text-[#124242] font-medium border-t mt-1 pt-1">
                            <span className="flex items-center gap-1">
                              <Plus size={14} />
                              Add new...
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#124242] mb-1.5 block uppercase tracking-wide">
                        Per 1
                      </label>
                      <div className="h-12 flex items-center justify-center bg-[#124242]/10 rounded-lg border border-[#124242]/20">
                        <span className="text-base font-bold text-[#124242]">
                          {batchTiffinCount > 0 
                            ? formatQuantity((parseFloat(ing.batch_qty) || 0) / batchTiffinCount)
                            : '0'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop Layout */}
              <div className="hidden md:grid grid-cols-12 gap-2 items-center">
                <div className="col-span-1 text-stone-400 flex justify-center">
                  <GripVertical size={18} />
                </div>
                
                <div className="col-span-4">
                  <input
                    type="text"
                    value={ing.ingredient_name}
                    onChange={(e) => updateIngredientLine(index, 'ingredient_name', e.target.value)}
                    className="virsa-input h-10"
                    placeholder="Type ingredient name"
                    list="ingredient-names"
                  />
                </div>
                
                <div className="col-span-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={ing.batch_qty}
                    onChange={(e) => updateIngredientLine(index, 'batch_qty', e.target.value)}
                    className="virsa-input h-10 text-center font-semibold"
                    placeholder="0"
                  />
                </div>
                
                <div className="col-span-2">
                  <Select
                    value={ing.unit || ''}
                    onValueChange={(v) => {
                      if (v === '__add_new__') {
                        setPendingUnitIndex(index);
                        setNewUnitName('');
                        setShowNewUnitDialog(true);
                      } else {
                        updateIngredientLine(index, 'unit', v);
                      }
                    }}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select unit">
                        {ing.unit || 'Select unit'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {availableUnits.map(unit => (
                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                      ))}
                      <SelectItem value="__add_new__" className="text-[#124242] font-medium border-t mt-1 pt-1">
                        <span className="flex items-center gap-1">
                          <Plus size={14} />
                          Add new unit...
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="col-span-2 text-center">
                  <span className="text-sm font-bold text-[#124242]">
                    {batchTiffinCount > 0 
                      ? formatQuantity((parseFloat(ing.batch_qty) || 0) / batchTiffinCount)
                      : '0'
                    }
                  </span>
                </div>
                
                <div className="col-span-1 text-right">
                  <button
                    onClick={() => removeIngredientLine(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add More Button */}
        <div className="flex justify-center mt-6">
          <button
            onClick={addIngredientLines}
            className="btn-outline"
            data-testid="add-ingredient-line-btn"
          >
            <Plus size={18} />
            Add 5 More Items
          </button>
        </div>

        {/* Datalist only for desktop */}
        <datalist id="ingredient-names">
          {ingredients.map(ing => (
            <option key={ing.id} value={ing.name} />
          ))}
        </datalist>

        <p className="text-xs text-stone-500 mt-4 text-center">
          Recipe will be saved with per-1-tiffin quantities (shown in green). New ingredients auto-add to master list.
        </p>
      </div>

      {/* Cooking Steps */}
      <div className="virsa-card p-6">
        <h2 className="text-xl font-bold text-stone-800 mb-4">Cooking Steps</h2>
        <textarea
          value={cookingSteps}
          onChange={(e) => setCookingSteps(e.target.value)}
          className="virsa-input min-h-[150px] resize-y"
          placeholder="Step 1: Soak dal overnight...&#10;Step 2: Pressure cook for 20 mins...&#10;Step 3: Prepare tempering..."
          data-testid="cooking-steps-input"
        />
      </div>

      {/* Important Notes */}
      <div className="virsa-card p-6 border-[#faa023]/30 bg-[#faa023]/5">
        <h2 className="text-xl font-bold text-stone-800 mb-4">Important Notes</h2>
        <textarea
          value={importantNotes}
          onChange={(e) => setImportantNotes(e.target.value)}
          className="virsa-input min-h-[100px] resize-y"
          placeholder="Any special instructions, tips, or warnings..."
          data-testid="important-notes-input"
        />
      </div>

      {/* Bottom Save Button */}
      <div className="flex justify-end gap-3 pb-8">
        <button
          onClick={() => navigate('/recipes')}
          className="btn-outline"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
          data-testid="save-recipe-btn-bottom"
        >
          {saving ? <span className="spinner" /> : <Save size={20} />}
          <span>{isEditing ? 'Update Recipe' : 'Save Recipe'}</span>
        </button>
      </div>

      {/* New Unit Dialog */}
      <Dialog open={showNewUnitDialog} onOpenChange={setShowNewUnitDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Unit</DialogTitle>
            <DialogDescription>
              Create a new unit of measurement. This will be available for all recipes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">
                Unit Name
              </label>
              <input
                type="text"
                value={newUnitName}
                onChange={(e) => setNewUnitName(e.target.value)}
                className="virsa-input"
                placeholder="e.g., bunch, pinch, dash"
                autoFocus
                data-testid="new-unit-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddNewUnit();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowNewUnitDialog(false);
                  setNewUnitName('');
                  setPendingUnitIndex(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddNewUnit}
                disabled={!newUnitName.trim()}
                className="bg-[#124242] hover:bg-[#124242]/90"
                data-testid="confirm-new-unit-btn"
              >
                Add Unit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Category Dialog */}
      <Dialog open={showNewCategoryDialog} onOpenChange={setShowNewCategoryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
            <DialogDescription>
              Create a new recipe category. This will be available for all recipes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">
                Category Name
              </label>
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="virsa-input"
                placeholder="e.g., Breakfast, Snack, Beverage"
                autoFocus
                data-testid="new-category-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddNewCategory();
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowNewCategoryDialog(false);
                  setNewCategoryName('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddNewCategory}
                disabled={!newCategoryName.trim()}
                className="bg-[#124242] hover:bg-[#124242]/90"
                data-testid="confirm-new-category-btn"
              >
                Add Category
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
